import React, { FC, useState } from 'react';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as DeleteIcon } from 'assets/icons/remove-modal.svg';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { getFullName, handleApiError } from 'utils/functions';
import LocalModal from 'components/base/local-modal';
import useDashboardContext from 'contexts/dashboard.context';
import { MemberListType } from 'models/member.model';

interface DeletePeopleProps {
  show: boolean;
  onClose: (withRes?: boolean) => void;
  selectedPeople?: MemberListType[];
  setSelectedPeople?: (value: MemberListType[]) => void;
  activeMember?: MemberListType | null;
  isAdmin?: boolean;
  onRefetch?: () => Promise<void>;
  removeMembers?: (ids: string[]) => void;
}

interface DeletePeopleFooterProps {
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeletePeopleFooter: FC<DeletePeopleFooterProps> = ({ onCancel, onConfirm, loading }) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button small color='primary' variant='outline' className='mx-auto' onClick={onCancel}>
      Cancel
    </Button>
    <Button
      small
      color='primary'
      variant='block'
      className='mx-auto'
      loading={loading}
      onClick={onConfirm}>
      Confirm
    </Button>
  </div>
);

const DeletePeople: FC<DeletePeopleProps> = ({
  show,
  onClose,
  selectedPeople,
  activeMember,
  setSelectedPeople,
  isAdmin,
  onRefetch,
  removeMembers
}) => {
  const [loading, setLoading] = useState(false);
  const [removeAdminLoading, setRemoveAdminLoading] = useState(false);
  const { activeCompany } = useDashboardContext();
  const companyId: string = activeCompany?.id;

  const confirmBulkDelete = async () => {
    if (!companyId) return;
    setLoading(true);
    const ids = selectedPeople?.map((item) => item.id);
    try {
      apiService.post('member/delete', {
        data: {
          ids
        }
      });

      toast(<ToastMessage type='success' message='Member(s) deleted successfully' />);
      onClose();
      setSelectedPeople?.([]);
      if (ids) removeMembers?.(ids);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };
  const confirmSingleDelete = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await apiService.delete(`member/${activeMember?.id}`);
      if (res) {
        toast(<ToastMessage type='success' message='Member deleted successfully' />);
        if (activeMember?.id) removeMembers?.([activeMember?.id]);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const removeAdmin = async () => {
    if (!companyId || removeAdminLoading) return;
    setRemoveAdminLoading(true);
    try {
      await apiService.put('member/update', {
        data: {
          department_id: activeMember?.department_id,
          role: 'MEMBER',
          member_id: activeMember?.id,
          company_id: companyId
        }
      });
      toast(<ToastMessage type='success' message='Admin removed successfully' />);
      onRefetch?.();
    } catch (err) {
      handleApiError(err);
    } finally {
      setRemoveAdminLoading(false);
      onClose();
    }
  };
  const user = activeMember?.user;
  return (
    <LocalModal
      isOpen={show}
      title=''
      titleIconColor='danger'
      onRequestClose={() => onClose()}
      footer={
        <DeletePeopleFooter
          loading={loading || removeAdminLoading}
          onCancel={() => onClose()}
          onConfirm={() => {
            if (isAdmin) {
              removeAdmin();
            } else {
              if (selectedPeople?.length) {
                confirmBulkDelete();
              } else {
                confirmSingleDelete();
              }
            }
          }}
        />
      }>
      {selectedPeople && selectedPeople?.length > 1 ? (
        <>
          <div className='flex flex-col justify-center items-center'>
            <DeleteIcon className='text-red-5 mx-auto mb-4' />
            <p className='text-[#2E3031] text-center mb-1'>Delete selected persons</p>
            <p className='text-[#6F7376] text-center'>
              This will delete all info associated with these persons
            </p>
          </div>
        </>
      ) : (
        <div className=''>
          <DeleteIcon className='text-red-5 mx-auto mb-4' />
          <p className='text-[18px] font-medium text-[#2E3031] text-center mb-1'>
            {isAdmin && `Remove ${getFullName(user)}`}
          </p>
          <p className='text-[#2E3031] text-center mb-1'>Are you sure you want to remove</p>
          <div className='flex gap-1 justify-center'>
            <p className='text-[#2E3031] text-center mb-1 capitalize'>{getFullName(user)}</p>
            <p>{isAdmin && 'as an admin?'}</p>
          </div>
        </div>
      )}
    </LocalModal>
  );
};

export default DeletePeople;
